import React, { useEffect } from "react";

import { useData, useDataValue, useErrors } from "../hooks/useData";
import { Icon } from "../ui/icons";
import { closest } from "../utils/dom";
import { List } from "./List";
import { newID } from "../utils/unique";
import { useGlobalHandler } from "../hooks/useGlobalHandler";
import { ErrorsDisplay } from "./Errors";
import { Label } from "./Label";
import { FormControlProps } from "./types";

export function Dropdown<T>(props: FormControlProps & {
    items: T[],

	alignTop?: boolean,

	multiSelect?: boolean,
	oneSelectionAtTime?: boolean, // when multiselect you choose one at a time and dropdown closes after each selection

	blankOption?: {
		value: any,
	}

	getItems?: () => Promise<any>;

    getValue: (item: any) => any;
    getDisplay: (item: T, selected: boolean) => any;
	renderSelection?: (items: T[]) => any,

    onChange?: (e: any, value: any) => void;
}) {
	
	const data = useData();
	const value = useDataValue(data, props.prop);
	const errors = useErrors(data, props.prop, props.errorsDisabled);
	const hasErrors = errors && errors.length > 0;

	const id = React.useRef<string>();
	if (!id.current) id.current = newID();

	const [state, setState] = React.useState({
		droppedDown: false,
		items: undefined,
	});

	useEffect(() => {
		if (!props.getItems) return;

		const p = props.getItems();
		p.then((items) => {
			setState({
				...state,
				items,
			});
		}).catch((e) => {

		});
	}, [props.getItems]);


	useGlobalHandler("mousedown", React.useCallback((e: any) => {
		// was click outside of dropdown?
		const el = closest(e.target, "." + id.current);
		if (!el) {
			if (state.droppedDown) {
				setState({
					...state,
					droppedDown: false,
				});
			}
		}
	}, [state]));

    function getValue(item: any) {
        if (props.getValue) {
            return props.getValue(item);
        } else {
            return item.value;
        }
    }

    function handleClick(e: any) {
        if (disabled) return;

		if (!state.droppedDown) {
			setState({
				...state, 
				droppedDown: true,
			});
		}

	}

    const {
        className,
        disabled,
        getDisplay,
    } = props;

	function renderSelection(selected: T[]) {
		const result = [];
		let i = 0;
		for(let o of selected) {
			result.push(
				<div className="option padding-2 tag selected-option" key={i++}>
					{getDisplay(o, true)}
				</div>
			);
		}
		return (
			<div className="flex horiz wrap gap-2">
				{result}
			</div>
		)
	}

	// 
    const selected = [];

	let items = props.items;
	if (state && state.items) items = state.items;

	if (items) {
        for(var i=0;i<items.length;i++) {
            const option = items[i];
			const optionValue = getValue(option);

			if (props.multiSelect) {
				if (value && value.indexOf(optionValue) >= 0) {
					selected.push(option);
				}
			} else {
				if (value === optionValue) {
					selected.push(option);
					break;
				}
			}

        }
    }

	let list;
	if (state.droppedDown) {
		list = (
			<List
				getValue={props.getValue}
				getDisplay={props.getDisplay}
				items={items}
				prop={props.prop}

				blankOption={props.blankOption}

				multiSelect={props.multiSelect}

				className="absolute left-0 bg shadow z-popup"
				style={{
					maxHeight: "400px",
					overflow: "scroll",
				}}

				onChange={(e, value) => {
					if (
						(
							props.multiSelect
							// leave open to allow for multiple selections
							// but only if there is more than one item
							&& (items.length <= 1 || props.oneSelectionAtTime)
						) 
						|| !props.multiSelect
					) {
						// close
						setState({
							...state,
							droppedDown: false,
						});
					}
					
					if (props.onChange) props.onChange(e, value);
				}}
			/>
		)	
	}

	let currentSelection = props.renderSelection ? props.renderSelection(selected) : renderSelection(selected);

	const arrow = (state.droppedDown) ?
		<Icon type="arrowUp" />
		: <Icon type="arrowDown" />
	;

	// if no selection to show make sure control has a min width
	if (!selected || selected.length <= 0) {
		const placeholder = props.placeholder ? props.placeholder : "";
		currentSelection = <div key="empty" className="padding-2 opacity-third" style={{minWidth:"100px"}}>{placeholder}</div>;
	}

	const component = (
		<div
            className={[
				className, 
				id.current,
				"dropdown form-control dropdown relative clickable bg", 
				disabled ? "disabled" : null,
				props.alignTop ? "align-top" : null,
			].join(" ")}
			style={props.style}
            onClick={handleClick}
            tabIndex={0}

			onKeyDown={(e) => {
				// try and jump to item starting with this 
				const k = e.key.toLowerCase();

				if (k === "enter") {
					if (state.droppedDown) {
						// close
						setState({
							...state,
							droppedDown: false,
						});
					}
					return;
				}

				if (!props.multiSelect) {
					for(let item of items) {
						const text = getDisplay(item, false);
						if (text && text.substring && text.substring(0, 1).toLowerCase() === k) {
							data.setValue(props.prop, getValue(item));
							break;
						}
					}
				}
			}}

        >
			<div className="flex horiz">
				{currentSelection}
				<div className="margin-left-5 padding-2">
					{arrow}
				</div>
			</div>
            {list}
        </div>
	);

	let label;
	if (props.title) {
		label = 
			<Label
				title={props.title}
				isRequired={(props.def && props.def.options) ? props.def.options.required : false}
				className={(hasErrors ? "has-errors" : "")}
			/>;
	}

	return (
		<div className="flex group align-start">
			{label}
			<div className="">
				{component}
				<ErrorsDisplay errors={errors} onlyDisplayItems={!props.displayAllErrors} />
			</div>
		</div>
    );


}

